<template>
  <a-spin :spinning="loading">
    <div class="space-y-6">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200">
          <Coins class="w-4 h-4 text-primary" aria-hidden="true" />
          {{ t('shell.account.tabRecharge') }}
        </div>
        <a-button @click="emit('openOrder')">
          <div class="inline-flex items-center gap-1">
            <Receipt class="w-4 h-4" aria-hidden="true" />
            {{ t('shell.account.order.view') }}
          </div>
        </a-button>
      </div>

      <div class="flex items-center justify-between rounded-xl bg-primary-50/60 dark:bg-primary-900/20 px-4 py-3">
        <span class="text-sm text-gray-600 dark:text-gray-300">{{ t('shell.account.balance') }}</span>
        <span class="text-xl font-bold text-primary">
          {{ balanceText }}
          <span class="ml-1 text-sm font-normal text-primary/70">{{ t('shell.account.pointsUnit') }}</span>
        </span>
      </div>

      <div>
        <div class="mb-3 text-sm font-medium text-gray-700 dark:text-gray-200">{{ t('shell.account.recharge.package') }}</div>
        <div v-if="info.packages.length === 0" class="text-sm text-gray-400">{{ t('shell.account.recharge.emptyPackage') }}</div>
        <!-- 套餐选项本质是一行「选项」：单列横向排布比双列卡片更省高度，也不会在窄屏被压缩折行 -->
        <div v-else class="space-y-2" role="radiogroup">
          <div
            v-for="p in info.packages"
            :key="p.id"
            class="flex items-center justify-between gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors"
            :class="selectedPackageId === p.id
              ? 'border-primary bg-primary/5 dark:bg-primary/10'
              : 'border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:bg-gray-50 dark:hover:bg-gray-800/40'"
            role="radio"
            :aria-checked="selectedPackageId === p.id"
            tabindex="0"
            @click="selectedPackageId = p.id"
            @keydown.enter.prevent="selectedPackageId = p.id"
            @keydown.space.prevent="selectedPackageId = p.id"
          >
            <div class="min-w-0">
              <div class="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{{ p.title }}</div>
              <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {{ t('shell.account.recharge.credit') }} {{ p.credit }}
                <span v-if="p.bonusCredit > 0">+ {{ t('shell.account.recharge.bonus') }} {{ p.bonusCredit }}</span>
              </div>
            </div>
            <div class="flex shrink-0 items-center gap-3">
              <div class="text-right">
                <div class="text-lg font-bold leading-tight text-primary">¥{{ formatPrice(p.price) }}</div>
                <div v-if="p.marketPrice > p.price" class="text-xs leading-tight text-gray-400 line-through">
                  ¥{{ formatPrice(p.marketPrice) }}
                </div>
              </div>
              <Check v-if="selectedPackageId === p.id" class="w-4 h-4 text-primary" aria-hidden="true" />
              <span v-else class="inline-block w-4 h-4" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>

      <div v-if="info.packages.length > 0 && info.channels.length !== 1">
        <div class="mb-3 text-sm font-medium text-gray-700 dark:text-gray-200">{{ t('shell.account.recharge.channel') }}</div>
        <a-radio-group v-if="info.channels.length > 1" v-model:value="payType">
          <a-radio v-for="ch in info.channels" :key="ch" :value="ch">{{ channelLabel(ch) }}</a-radio>
        </a-radio-group>
        <div v-else class="text-sm text-gray-400">{{ t('shell.account.recharge.noChannel') }}</div>
      </div>

      <div>
        <a-button
          type="primary"
          size="large"
          block
          :loading="submitting"
          :disabled="!selectedPackageId || !payType"
          @click="handleSubmit"
        >
          {{ t('shell.account.recharge.submit') }}
        </a-button>
        <div v-if="singleChannelHint" class="mt-2 text-center text-xs text-gray-400 dark:text-gray-500">
          {{ singleChannelHint }}
        </div>
        <div v-if="rechargeAgreement" class="mt-2 text-center text-xs text-gray-400 dark:text-gray-500">
          {{ t('shell.account.recharge.agreementPrefix') }}
          <a-button type="text" class="!p-0 !h-auto text-xs" @click="emit('update:agreementOpen', true)">
            {{ t('shell.account.recharge.agreementName') }}
          </a-button>
        </div>
      </div>
    </div>

    <AccountRechargeQrModal
      v-model:open="qrOpen"
      :order-no="qrOrder.orderNo"
      :pay-url="qrOrder.payUrl"
      :qr-code="qrOrder.qrCode"
      :amount="qrOrder.amount"
      @success="handlePaid"
    />
    <AccountRechargeAgreementModal
      :open="agreementOpen"
      :content="rechargeAgreement"
      @update:open="emit('update:agreementOpen', $event)"
    />
  </a-spin>
</template>

<script setup lang="ts">
import { message } from 'ant-design-vue'
import { Check, Coins, Receipt } from 'lucide-vue-next'
import { computed, onMounted, reactive, ref } from 'vue'
import { getAgreementInfo } from '../../api/auth'
import { getRechargeInfo, submitRecharge, type RechargeInfo } from '../../api/user/recharge'
import { useI18n } from '../../composables/useI18n'
import { useTestActions } from '../../composables/useTestAction'
import AccountRechargeAgreementModal from './AccountRechargeAgreementModal.vue'
import AccountRechargeQrModal from './AccountRechargeQrModal.vue'

// 协议弹窗的开关由父组件（AccountModal）控制，便于测试动作统一触发
withDefaults(defineProps<{ agreementOpen?: boolean }>(), { agreementOpen: false })
const emit = defineEmits<{ openOrder: []; 'update:agreementOpen': [value: boolean] }>()

const { t } = useI18n()
const loading = ref(false)
const submitting = ref(false)
const selectedPackageId = ref<number | null>(null)
const payType = ref('')
const info = reactive<RechargeInfo>({ packages: [], channels: [], credit: 0 })
const rechargeAgreement = ref('')

// 扫码支付弹窗状态（Connect 收银台地址或直连渠道二维码内容）
const qrOpen = ref(false)
const qrOrder = reactive({ orderNo: '', payUrl: '', qrCode: '', amount: 0 })

const balanceText = computed(() => Number(info.credit || 0).toLocaleString('en-US'))
// 仅单一渠道时：在按钮下方提示实际支付方式；Connect 为聚合支付，额外说明支持的扫码渠道
const singleChannelHint = computed(() => {
  if (info.channels.length !== 1) return ''
  const ch = info.channels[0]
  const name = channelLabel(ch)
  return ch === 'connect' ? `${name} · ${t('shell.account.recharge.connectHint')}` : name
})

const CHANNEL_LABEL_KEYS: Record<string, string> = {
  alipay: 'shell.account.channel.alipay',
  wechat: 'shell.account.channel.wechat',
  connect: 'shell.account.channel.connect',
}
function channelLabel(ch: string) {
  return CHANNEL_LABEL_KEYS[ch] ? t(CHANNEL_LABEL_KEYS[ch]) : ch
}

/** 价格统一保留两位小数，避免 ¥6 与 ¥6.3 混排 */
function formatPrice(value: number) {
  return Number(value || 0).toFixed(2)
}

async function load() {
  loading.value = true
  try {
    const data = await getRechargeInfo()
    info.packages = data.packages || []
    info.channels = data.channels || []
    info.credit = data.credit
    if (!selectedPackageId.value && info.packages.length) selectedPackageId.value = info.packages[0].id
    if (!payType.value && info.channels.length) payType.value = info.channels[0]
    try {
      rechargeAgreement.value = (await getAgreementInfo()).rechargeAgreement || ''
    } catch {
      rechargeAgreement.value = ''
    }
  } finally {
    loading.value = false
  }
}

function isMobileDevice() {
  return window.matchMedia('(max-width: 640px)').matches
}

async function handleSubmit() {
  if (!selectedPackageId.value || !payType.value) return
  const selected = info.packages.find((p) => p.id === selectedPackageId.value)
  submitting.value = true
  try {
    const res = await submitRecharge(selectedPackageId.value, payType.value)
    // Connect 返回收银台地址，直连渠道返回二维码内容，统一编码为二维码展示
    const qrPayload = (res.qrCode || res.payUrl || '').trim()
    if (!qrPayload) {
      message.error(t('shell.account.recharge.failed'))
      return
    }
    // 手机端直接打开发起支付的收银台页面，避免让用户扫描自己屏幕上的二维码
    if (isMobileDevice() && res.payUrl) {
      window.location.href = res.payUrl
      return
    }
    Object.assign(qrOrder, {
      orderNo: res.orderNo,
      payUrl: res.payUrl || '',
      qrCode: res.qrCode || '',
      amount: Number(selected?.price || 0),
    })
    qrOpen.value = true
  } catch (e: any) {
    message.error(e?.message || t('shell.account.recharge.failed'))
  } finally {
    submitting.value = false
  }
}

function handlePaid(credit: number) {
  info.credit = credit
  qrOpen.value = false
  message.success(t('shell.account.recharge.success'))
}

// 截图脚本用：直接打开扫码弹窗并填充示例数据
useTestActions({
  'shell.account.rechargeQr.open': () => {
    Object.assign(qrOrder, {
      orderNo: 'TEST-ORDER-0001',
      payUrl: 'https://example.com/pay/test-order',
      qrCode: '',
      amount: 6,
    })
    qrOpen.value = true
  },
  'shell.account.rechargeQr.close': () => {
    qrOpen.value = false
  },
})

onMounted(load)
</script>
